import type { ReactNode } from "react";
import { useEffect } from "react";
import { useLocation } from "@tanstack/react-router";
import { SiteHeader } from "./SiteHeader";
import { SiteFooter } from "./SiteFooter";
import { EntryOfferBridge } from "./EntryOfferBridge";
import { USSiteNotice } from "./USSiteNotice";
import { captureAttribution } from "@/lib/analytics";
import { initWebVitalsMonitoring } from "@/lib/web-vitals";
import type { ChallengeId, ScopeIntentId } from "@/lib/quote-links";

type EntryRoute = {
  intent: ScopeIntentId;
  challenge: ChallengeId;
};

type PublicPageType =
  | "home"
  | "services"
  | "money"
  | "pricing"
  | "retainer"
  | "geo"
  | "resources-index"
  | "resource-hub"
  | "resource-article"
  | "work"
  | "trust"
  | "about"
  | "contact"
  | "quote"
  | "audit"
  | "blog"
  | "legal"
  | "partner"
  | "landing"
  | "content"
  | "application";

const ENTRY_ROUTING: Record<string, EntryRoute> = {
  "/shopify-experts": { intent: "rebuild", challenge: "custom_features" },
  "/es/desarrollo-shopify": { intent: "rebuild", challenge: "custom_features" },
  "/shopify-migration": { intent: "rebuild", challenge: "migrating" },
  "/es/migracion-shopify": { intent: "rebuild", challenge: "migrating" },
  "/shopify-performance": { intent: "improve", challenge: "slow" },
  "/es/rendimiento": { intent: "improve", challenge: "slow" },
  "/shopify-cro": { intent: "improve", challenge: "conversion_friction" },
  "/es/cro-shopify": { intent: "improve", challenge: "conversion_friction" },
  "/shopify-technical-seo": { intent: "improve", challenge: "technical_seo_visibility" },
  "/es/seo-tecnico-shopify": { intent: "improve", challenge: "technical_seo_visibility" },
  "/shopify-integration-services": { intent: "rebuild", challenge: "custom_features" },
  "/es/integraciones-shopify": { intent: "rebuild", challenge: "custom_features" },
  "/shopify-app-development": { intent: "rebuild", challenge: "custom_features" },
  "/es/desarrollo-apps-shopify": { intent: "rebuild", challenge: "custom_features" },
  "/analytics-ai": { intent: "improve", challenge: "measurement_data" },
  "/es/analitica-ai": { intent: "improve", challenge: "measurement_data" },
  "/shopify-plus-agency": { intent: "rebuild", challenge: "custom_features" },
  "/es/agencia-shopify-plus": { intent: "rebuild", challenge: "custom_features" },
  "/es/shopify-plus-mexico": { intent: "rebuild", challenge: "custom_features" },
};

const OTHER_MONEY_PATHS = new Set([
  "/shopify-development",
  "/es/shopify-development",
  "/shopify-marketing-strategy",
  "/es/estrategia-marketing-shopify",
  "/cro-seo",
  "/es/cro-seo",
  "/performance",
]);

const RESOURCE_HUB_PATHS = new Set([
  "/resources/performance",
  "/resources/conversion",
  "/resources/technical-seo",
  "/resources/migration",
  "/es/recursos/rendimiento",
  "/es/recursos/conversion",
  "/es/recursos/seo-tecnico",
  "/es/recursos/migracion",
]);

const APPLICATION_PREFIXES = [
  "/admin",
  "/portal",
  "/agency",
  "/partner-login",
  "/login",
  "/reset-password",
  "/checkout",
  "/order-confirmed",
];

function resolvePageType(path: string): PublicPageType {
  if (APPLICATION_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) return "application";
  if (path === "/" || path === "/es") return "home";
  if (path === "/services" || path === "/es/servicios") return "services";
  if (path === "/pricing" || path === "/es/precios") return "pricing";
  if (path === "/retainer" || path === "/es/retainer") return "retainer";
  if (["/geo-readiness", "/es/auditoria-geo", "/shugert-geo"].includes(path)) return "geo";
  if (path === "/resources" || path === "/es/recursos") return "resources-index";
  if (RESOURCE_HUB_PATHS.has(path)) return "resource-hub";
  if (path.startsWith("/resources/") || path.startsWith("/es/recursos/")) return "resource-article";
  if (ENTRY_ROUTING[path] || OTHER_MONEY_PATHS.has(path)) return "money";
  if (path === "/work" || path === "/es/proyectos") return "work";
  if (path === "/trust" || path === "/es/confianza") return "trust";
  if (path === "/about" || path.startsWith("/about/") || path === "/es/nosotros" || path.startsWith("/es/nosotros/")) return "about";
  if (path === "/contact" || path === "/es/contacto") return "contact";
  if (path === "/get-a-quote" || path === "/es/cotizacion") return "quote";
  if (path === "/audit" || path.startsWith("/audit/") || path === "/es/auditoria" || path.startsWith("/es/auditoria/")) return "audit";
  if (path === "/blog" || path.startsWith("/blog/") || path === "/es/blog" || path.startsWith("/es/blog/")) return "blog";
  if (["/privacy", "/privacy-policy", "/terms", "/terms-and-conditions.html", "/es/privacidad", "/es/terminos"].includes(path)) return "legal";
  if (path === "/partners" || path.startsWith("/partners/") || path.startsWith("/es/partners/")) return "partner";
  if (path.startsWith("/lp/") || path.startsWith("/es/lp/")) return "landing";
  return "content";
}

export function SiteShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const path = location.pathname.replace(/\/$/, "") || "/";
  const entryRoute = ENTRY_ROUTING[path];
  const locale = path.startsWith("/es/") || path === "/es" ? "es" : "en";
  const pageType = resolvePageType(path);
  const publicSurface = pageType === "resources-index" || pageType === "resource-hub" || pageType === "resource-article"
    ? "resource"
    : ["money", "pricing", "retainer", "geo"].includes(pageType)
      ? "money"
      : "general";

  useEffect(() => {
    captureAttribution();
  }, [path]);

  useEffect(() => {
    initWebVitalsMonitoring();
  }, []);

  return (
    <>
      <div
        className={`public-institutional public-page public-page--${pageType} flex min-h-dvh flex-col bg-background text-foreground`}
        data-public-surface={publicSurface}
        data-page-type={pageType}
        data-locale={locale}
        data-route={path}
      >
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-foreground focus:px-4 focus:py-2 focus:text-background"
        >
          Skip to main content
        </a>
        <SiteHeader />
        {pageType !== "application" ? <USSiteNotice locale={locale} /> : null}
        <main id="main-content" className="flex-1 pt-[88px] md:pt-[104px]">
          {children}
          {entryRoute ? (
            <EntryOfferBridge
              locale={locale}
              path={path}
              intent={entryRoute.intent}
              challenge={entryRoute.challenge}
            />
          ) : null}
        </main>
        <SiteFooter />
      </div>
    </>
  );
}
